import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import prestigeCardService from "../../../services/prestigeCardService";
import {
    Container,
    Box,
    Typography,
    Breadcrumbs,
    TextField,
    Button,
    Paper,
    Grid,
} from "@mui/material";
import { instanceToken } from "../../../utils/constant";
import LoadingOverlay from "../../../components/LoadingOverlay";

const UpdatePrestigeCard = ({ homeAlert }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [formValues, setFormValues] = useState({
        name: "",
        customer_id: "",
        club_id: "",
        valid_until: "",
        policy_no: "",
        policyholder_name: "",
        nrc_no: "",
        name_mm: "",
        sum_assured: "",
        issued_date: "",
        agent_name: "",
        agent_type: "",
        group_team: "",
        channel: "",
        relationship: "",
        tier_status: "",
        points_balance: "",
        remark: "",
        card_status: "",
    });

    useEffect(() => {
        const fetchCard = async () => {
            try {
                const data = await prestigeCardService.getPrestigeCard(instanceToken.token, id);
                setFormValues({
                    name: data.name || "",
                    customer_id: data.customer_id || "",
                    club_id: data.club_id || "",
                    valid_until: data.valid_until || "",
                    policy_no: data.policy_no || "",
                    policyholder_name: data.policyholder_name || "",
                    nrc_no: data.nrc_no || "",
                    name_mm: data.name_mm || "",
                    sum_assured: data.sum_assured || "",
                    issued_date: data.issued_date || "",
                    agent_name: data.agent_name || "",
                    agent_type: data.agent_type || "",
                    group_team: data.group_team || "",
                    channel: data.channel || "",
                    relationship: data.relationship || "",
                    tier_status: data.tier_status || "",
                    points_balance: data.points_balance || "",
                    remark: data.remark || "",
                    card_status: data.card_status || "",
                });
                setLoading(false);
            } catch (error) {
                console.error(error);
                homeAlert("Error fetching card details!", true);
                navigate("/prestige-cards");
            }
        };
        fetchCard();
    }, [id, instanceToken.token, homeAlert, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            await prestigeCardService.updatePrestigeCard(instanceToken.token, id, formValues);
            homeAlert("Prestige Card updated successfully!", false);
            navigate("/prestige-cards");
        } catch (error) {
            console.error(error);
            homeAlert("Update failed!", true);
        }
    };

    if (loading) return <LoadingOverlay message="Loading card details..." />;

    return (
        <>
            <div role="presentation">
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" to="/prestige-cards">
                        Prestige Card
                    </Link>
                    <Typography color="text.primary">Edit</Typography>
                </Breadcrumbs>
            </div>

            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 4, borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: "#1976d2", mb: 3 }}>
                        Edit Prestige Card
                    </Typography>
                    <form onSubmit={handleUpdate}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Name (English)"
                                    name="name"
                                    value={formValues.name}
                                    onChange={handleChange}
                                    required
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Name (Myanmar)"
                                    name="name_mm"
                                    value={formValues.name_mm}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Club ID / Card ID"
                                    name="club_id"
                                    value={formValues.club_id}
                                    onChange={handleChange}
                                    required
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Customer ID"
                                    name="customer_id"
                                    value={formValues.customer_id}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="NRC No"
                                    name="nrc_no"
                                    value={formValues.nrc_no}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Policy No"
                                    name="policy_no"
                                    value={formValues.policy_no}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Policyholder Name"
                                    name="policyholder_name"
                                    value={formValues.policyholder_name}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Sum Assured"
                                    name="sum_assured"
                                    value={formValues.sum_assured}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Issued Date"
                                    name="issued_date"
                                    value={formValues.issued_date}
                                    onChange={handleChange}
                                    variant="outlined"
                                    placeholder="YYYY-MM-DD"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Valid Until"
                                    name="valid_until"
                                    value={formValues.valid_until}
                                    onChange={handleChange}
                                    required
                                    variant="outlined"
                                    placeholder="YYYY-MM-DD"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Agent Name"
                                    name="agent_name"
                                    value={formValues.agent_name}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Agent Type"
                                    name="agent_type"
                                    value={formValues.agent_type}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Group / Team"
                                    name="group_team"
                                    value={formValues.group_team}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Channel"
                                    name="channel"
                                    value={formValues.channel}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Relationship"
                                    name="relationship"
                                    value={formValues.relationship}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Card Status"
                                    name="card_status"
                                    value={formValues.card_status}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Tier Status"
                                    name="tier_status"
                                    value={formValues.tier_status}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Points Balance"
                                    name="points_balance"
                                    value={formValues.points_balance}
                                    onChange={handleChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Remark"
                                    name="remark"
                                    value={formValues.remark}
                                    onChange={handleChange}
                                    multiline
                                    rows={2}
                                    variant="outlined"
                                />
                            </Grid>
                        </Grid>
                        <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "flex-end" }}>
                            <Button variant="outlined" component={Link} to="/prestige-cards">
                                Cancel
                            </Button>
                            <Button variant="contained" type="submit" sx={{ backgroundColor: "#1976d2" }}>
                                Save Changes
                            </Button>
                        </Box>
                    </form>
                </Paper>
            </Container>
        </>
    );
};

export default UpdatePrestigeCard;
